import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http'
import { Observable } from 'rxjs';
import { Locale } from './Locale';
import { AppConfig } from '../../../../assets/config/appconfig';
import { map, shareReplay } from 'rxjs/operators';

//Create a cache service so that the locale strings can be cached on the client side based on language
const cacheableCache: { [key: string]: Observable<any> } = {};
export function cacheable<T>(
  returnObservable: Observable<T>, 
  key?: string, 
  customCache?: { [key: string]: Observable<T> }
): Observable<T> {
  const cache = customCache ?? cacheableCache;
  
  // Return cached observable if exists
  if (key && cache[key]) {
    return cache[key];
  }
  
  // Use shareReplay to cache the observable
  const shared = returnObservable.pipe(
    shareReplay({ bufferSize: 1, refCount: false })
  );
  
  // Store in cache if key provided
  if (key) {
    cache[key] = shared;
  }
  
  return shared;
}

@Injectable()
export class LocalesService {
    private localeURL: string;

    constructor(private http: HttpClient, private _appConfig: AppConfig) {
        this.localeURL = `${this._appConfig.settings.defaultURL}/v2/locale/`;
    }

    getLocalesByLang(lang: string): Observable<Locale[]> {
        return cacheable<Locale[]>(this.http.get<Locale[]>(`${this.localeURL}${lang}`), lang);
    }

    getLocaleStringObservable(lang: string, localeKey: string, localeView?: string): Observable<Locale> {
        return this.getLocalesByLang(lang).pipe(
            map((locales: Locale[]) => {
                const filteredLocale = locales.find((item) =>
                    item.key === localeKey &&
                    (!localeView || item.view === null || item.view === undefined || item.view === localeView)
                );
                return filteredLocale ?? {} as Locale;
            })
        );
    }

    getLocaleValue(lang: string, localeKey: string, localeView?: string): Observable<string> {
        return this.getLocaleStringObservable(lang, localeKey, localeView).pipe(
            map((locale: Locale) => locale.value ?? '')
        );
    }
}

//Create a class so that the client language being requested can be passed across components
@Injectable()
export class ConfigService {
    private localelang!: string;
    private action!: string;

    get languagerequested() {
        return this.localelang;
    }

    set languagerequested(value: string) {
        this.localelang = value;
    }

    get iframeAction() {
        return this.action;
    }

    set iframeAction(value: string) {
        this.action = value;
    }
}
